/**
 * Tipos para geolocalización y tracking GPS
 * Sprint 2 - POC GPS Test
 */

/**
 * Estructura normalizada de ubicación
 */
export interface LocationData {
    latitude: number;
    longitude: number;
    accuracy: number;       // metros
    timestamp: number;      // Unix timestamp ms
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;  // 0-360 grados
    speed?: number | null;    // m/s
    battery_level?: number | null; // Sprint 7: 0-100 porcentaje
}

/**
 * Estado de permisos de ubicación
 */
export type LocationPermissionStatus =
    | 'prompt'          // Usuario no ha decidido
    | 'granted'         // Permiso concedido
    | 'denied'          // Permiso denegado
    | 'denied-forever'; // "No volver a preguntar" (Android)

/**
 * Configuración para watch de ubicación
 */
export interface LocationWatchOptions {
    enableHighAccuracy?: boolean;
    timeout?: number;           // ms máximo para obtener ubicación
    maximumAge?: number;        // ms máximo de cache permitido
    interval?: number;          // ms entre actualizaciones (solo para polling)
}

/**
 * Errores específicos de ubicación
 */
export type LocationErrorCode =
    | 'PERMISSION_DENIED'
    | 'POSITION_UNAVAILABLE'
    | 'TIMEOUT'
    | 'GPS_DISABLED'
    | 'UNKNOWN';

export interface LocationError {
    code: LocationErrorCode;
    message: string;
}

/**
 * Estado global de ubicación
 */
export interface LocationState {
    // Estado actual
    location: LocationData | null;
    permissionStatus: LocationPermissionStatus;
    isWatching: boolean;
    error: LocationError | null;
    lastUpdated: number | null;

    // Flags de UI
    isLoading: boolean;
    isRequestingPermission: boolean;
}

/**
 * Payload para enviar ubicación al backend
 */
export interface LocationPayload {
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp?: number;
}

/**
 * Respuesta del backend al registrar ubicación
 */
export interface LocationApiResponse {
    message: string;
    data: {
        id: number;
        latitude: number;
        longitude: number;
        accuracy: number | null;
        recorded_at: string;
    };
}

/**
 * Constantes de configuración para GPS
 */
export const GPS_CONFIG = {
    // Watch options por defecto
    DEFAULT_OPTIONS: {
        enableHighAccuracy: true,
        timeout: 10000,     // 10 segundos
        maximumAge: 0,      // Sin cache
    } as const,

    // Intervalo de actualización para POC
    POC_INTERVAL_MS: 5000,  // 5 segundos

    // Umbral mínimo de precisión aceptable (metros)
    MIN_ACCURACY_THRESHOLD: 100,
} as const;
